import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { RESET_ALL as RESET_ON_LOGOUT, danger, success } from 'redux-store/reducers/common-reducer';

import { API, get, post, put } from 'utils/api';
import { createReducer } from 'utils/redux';
import { logError } from 'utils/log';
import { __t, applyTranslations } from 'utils/language';

const { PAYOUTS } = API.END_POINTS;

const PAYOUTS_BY_CAMPAIGN_LOADING = 'payouts/PAYOUTS_BY_CAMPAIGN_LOADING';
const PAYOUTS_BY_CAMPAIGN_LOADED = 'payouts/PAYOUTS_BY_CAMPAIGN_LOADED';
const PAYOUTS_BY_CAMPAIGN_RESET = 'payouts/PAYOUTS_BY_CAMPAIGN_RESET';

import type { Action, Dispatch, Payout, ThunkAction } from 'types';

applyTranslations(require('./campaign-payouts.i18n.json'));

const msgKey = 'campaign_payouts_reducer';

const initialState = {
  payoutsByCampaign: [],
  isLoading: false
};

const setLoadingStatus = (type: string, status: boolean): Action => ({
  type,

  payload: {
    status
  }
});

const payoutsByCampaignLoaded = (type: string, data: Array<Payout>): Action => ({
  type,

  payload: {
    data
  }
});

export const resetPayoutsByCampaign = (): Action => ({
  type: PAYOUTS_BY_CAMPAIGN_RESET
});

export const fetchPayoutsByCampaign = (campaignId: number): ThunkAction => async (dispatch: Dispatch) => {
  dispatch(setLoadingStatus(PAYOUTS_BY_CAMPAIGN_LOADING, true));

  try {
    const url = PAYOUTS.handlePayoutsByCampaign(campaignId);
    const response = await get(url);

    dispatch(payoutsByCampaignLoaded(PAYOUTS_BY_CAMPAIGN_LOADED, response.data));
  } catch (e) {
    dispatch(setLoadingStatus(PAYOUTS_BY_CAMPAIGN_LOADING, false));
    logError(e);
  }
};

export const releaseTotalPayouts = (campaignId: number, subcampaignId: number): ThunkAction => async (dispatch: Dispatch) => {
  const url = PAYOUTS.handleReleaseTotalPayouts(subcampaignId);

  dispatch(showLoading(url));

  try {
    await post(url);

    dispatch(fetchPayoutsByCampaign(campaignId));
    dispatch(hideLoading(url));
    dispatch(success(__t(`${msgKey}.messages.release_total_success`)));
  } catch (e) {
    dispatch(hideLoading(url));
    dispatch(danger(__t(`${msgKey}.messages.release_total_error`)));
    logError(e);
  }
};

export const releasePartialPayout = (
  payoutId: number,
  campaignId: number,
  subcampaignId: number,
  onRelease?: () => void
): ThunkAction => async (dispatch: Dispatch) => {
  const url = PAYOUTS.handleReleasePartialPayout(payoutId, subcampaignId);

  dispatch(showLoading(url));

  try {
    await post(url);

    if (onRelease) {
      onRelease();
    } else {
      dispatch(fetchPayoutsByCampaign(campaignId));
    }
    dispatch(hideLoading(url));
    dispatch(success(__t(`${msgKey}.messages.release_partial_success`)));
  } catch (e) {
    dispatch(hideLoading(url));
    dispatch(danger(__t(`${msgKey}.messages.release_partial_error`)));
    logError(e);
  }
};

export const unreleasePartialPayout = (
  payoutId: number,
  campaignId: number,
  subcampaignId: number,
  onUnrelease?: () => void
): ThunkAction => (
  async (dispatch: Dispatch) => {
    const url = PAYOUTS.handleUnreleasePartialPayout(payoutId, subcampaignId);

    dispatch(showLoading(url));

    try {
      await put(url);

      if (onUnrelease) {
        onUnrelease();
      } else {
        dispatch(fetchPayoutsByCampaign(campaignId));
      }

      dispatch(hideLoading(url));
      dispatch(success(__t(`${msgKey}.messages.unrelease_partial_success`)));
    } catch (e) {
      dispatch(hideLoading(url));
      dispatch(danger(__t(`${msgKey}.messages.unrelease_partial_error`)));
      logError(e);
    }
  }
);

export default createReducer<any, any>('campaignPayouts', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [PAYOUTS_BY_CAMPAIGN_RESET]: (state) => ({
    ...state,
    payoutsByCampaign: [],
    variablePaymentsByCampaign: []
  }),
  [PAYOUTS_BY_CAMPAIGN_LOADING]: (state, { status }) => ({
    ...state,
    isLoading: status
  }),
  [PAYOUTS_BY_CAMPAIGN_LOADED]: (state, { data }) => ({
    ...state,
    payoutsByCampaign: data,
    isLoading: false
  })
});
